import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import { ASSETS, SERVICES } from "@/lib/services";

const TAB_GROUPS = [
  { id: "site", label: "Site & Navigation", match: (k) => k.startsWith("site.") || k.startsWith("nav.") || k.startsWith("footer.") },
  { id: "hero", label: "Home — Hero", match: (k) => k.startsWith("hero.") },
  { id: "home", label: "Home — Sections", match: (k) => k.startsWith("home.") || k.startsWith("services.") },
  { id: "about", label: "About Page", match: (k) => k.startsWith("about.") },
  { id: "contact", label: "Contact Page", match: (k) => k.startsWith("contact.") },
  { id: "legal", label: "Cookies & Legal", match: (k) => k.startsWith("cookies.") || k.startsWith("cookie_policy.") || k.startsWith("privacy_policy.") || k.startsWith("terms.") },
  ...SERVICES.map((s) => ({
    id: `svc-${s.slug}`,
    label: `Service · ${s.slug.replace("-", " ")}`,
    match: (k) => k.startsWith(`service.${s.slug}.`),
  })),
];

function isLongField(key, value) {
  return (
    key.endsWith(".body") ||
    key.endsWith(".intro") ||
    key.endsWith(".subtitle") ||
    (typeof value === "string" && value.length > 140)
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { entries, reload } = useContent();
  const navigate = useNavigate();
  const [view, setView] = useState("content"); // "content" | "messages"
  const [activeTab, setActiveTab] = useState("site");
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [messages, setMessages] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    setDraft(entries);
  }, [entries]);

  const loadMessages = async () => {
    const { data } = await api.get("/contact");
    setMessages(data);
  };

  useEffect(() => {
    if (view === "messages") loadMessages();
  }, [view]);

  const currentTab = TAB_GROUPS.find((g) => g.id === activeTab);
  const visibleKeys = useMemo(() => {
    const keys = Object.keys(draft || {}).filter((k) => currentTab?.match(k));
    if (!search) return keys.sort();
    const s = search.toLowerCase();
    return keys
      .filter((k) => k.toLowerCase().includes(s) || (draft[k] || "").toLowerCase().includes(s))
      .sort();
  }, [draft, currentTab, search]);

  const dirty = useMemo(
    () => Object.keys(draft).some((k) => draft[k] !== entries[k]),
    [draft, entries]
  );

  const dirtyKeys = useMemo(
    () => Object.keys(draft).filter((k) => draft[k] !== entries[k]),
    [draft, entries]
  );

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg("");
    try {
      const patch = {};
      for (const k of dirtyKeys) patch[k] = draft[k];
      await api.put("/content", { lang: "en", entries: patch });
      await reload("en");
      setSaveMsg(`Saved ${dirtyKeys.length} change${dirtyKeys.length === 1 ? "" : "s"}.`);
      setTimeout(() => setSaveMsg(""), 3000);
    } catch (e) {
      setSaveMsg("Save failed — please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset all content to the original defaults? Your edits will be overwritten.")) return;
    await api.post("/content/reset?lang=en");
    await reload("en");
    setSaveMsg("Content reset to defaults.");
    setTimeout(() => setSaveMsg(""), 3000);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  const markRead = async (id) => {
    await api.patch(`/contact/${id}/read`);
    loadMessages();
  };

  const deleteMessage = async (id) => {
    if (!window.confirm("Delete this message permanently?")) return;
    await api.delete(`/contact/${id}`);
    loadMessages();
  };

  const unreadCount = messages.filter((m) => !m.read).length;

  return (
    <div className="min-h-screen bg-bone-100 text-ink-800 flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="w-72 bg-ink-800 text-bone-50 min-h-screen flex flex-col">
        <div className="p-6 border-b border-ink-600">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-sidebar-logo">
            <img src={ASSETS.logo} alt="VF" className="h-10 w-10 object-contain" />
            <div>
              <div className="font-serif text-base leading-tight">Vasileios Filippidis</div>
              <div className="uppercase tracking-[0.24em] text-[9px] text-gold-500">Control Panel</div>
            </div>
          </Link>
        </div>

        <nav className="p-4 space-y-1 flex-1">
          <button
            onClick={() => setView("content")}
            className={`w-full text-left px-4 py-3 text-sm uppercase tracking-[0.18em] flex items-center gap-3 ${
              view === "content" ? "bg-gold-500 text-ink-800" : "text-bone-100/80 hover:bg-ink-700"
            }`}
            data-testid="admin-tab-content"
          >
            <i className="fa-solid fa-pen-to-square w-4" /> Content
          </button>
          <button
            onClick={() => setView("messages")}
            className={`w-full text-left px-4 py-3 text-sm uppercase tracking-[0.18em] flex items-center justify-between ${
              view === "messages" ? "bg-gold-500 text-ink-800" : "text-bone-100/80 hover:bg-ink-700"
            }`}
            data-testid="admin-tab-messages"
          >
            <span className="flex items-center gap-3"><i className="fa-solid fa-inbox w-4" /> Messages</span>
            {unreadCount > 0 && (
              <span className="bg-gold-500 text-ink-800 px-2 py-0.5 text-[10px] font-medium" data-testid="admin-unread-badge">
                {unreadCount}
              </span>
            )}
          </button>

          {view === "content" && (
            <div className="mt-6 space-y-0.5">
              <div className="px-4 py-2 text-[10px] uppercase tracking-[0.24em] text-bone-100/40">Sections</div>
              {TAB_GROUPS.map((g) => (
                <button
                  key={g.id}
                  onClick={() => setActiveTab(g.id)}
                  className={`w-full text-left px-4 py-2 text-xs ${
                    activeTab === g.id ? "text-gold-500 border-l-2 border-gold-500" : "text-bone-100/70 hover:text-bone-50"
                  }`}
                  data-testid={`admin-section-${g.id}`}
                >
                  {g.label}
                </button>
              ))}
            </div>
          )}
        </nav>

        <div className="p-4 border-t border-ink-600">
          <div className="text-xs text-bone-100/60 mb-3">
            <i className="fa-solid fa-user-shield mr-2 text-gold-500" />
            {user?.email}
          </div>
          <button onClick={handleLogout} className="text-xs text-bone-100/70 hover:text-gold-500 uppercase tracking-[0.2em]" data-testid="admin-logout">
            <i className="fa-solid fa-right-from-bracket mr-2" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 p-10 overflow-x-hidden">
        {view === "content" && (
          <div data-testid="admin-content-view">
            <div className="flex items-end justify-between mb-10 gap-4 flex-wrap">
              <div>
                <div className="eyebrow mb-3">Manage Content</div>
                <h1 className="font-serif text-4xl">{currentTab?.label}</h1>
                <p className="mt-2 text-ink-800/60 text-sm">
                  Editing language: <strong>English</strong> · Edits go live immediately after Save.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="search"
                  placeholder="Search keys or text…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="border border-ink-800/15 px-4 py-2.5 text-sm w-64 focus:outline-none focus:border-gold-600"
                  data-testid="admin-content-search"
                />
                <button onClick={handleReset} className="btn-outline-ink text-xs" data-testid="admin-reset-defaults">
                  Reset to defaults
                </button>
                <button
                  onClick={handleSave}
                  disabled={!dirty || saving}
                  className="btn-gold disabled:opacity-50"
                  data-testid="admin-save-changes"
                >
                  {saving ? "Saving…" : dirty ? `Save ${dirtyKeys.length} change${dirtyKeys.length === 1 ? "" : "s"}` : "Saved"}
                </button>
              </div>
            </div>

            {saveMsg && (
              <div className="mb-6 px-4 py-3 bg-gold-500/15 border-l-4 border-gold-500 text-ink-800 text-sm" data-testid="admin-save-message">
                {saveMsg}
              </div>
            )}

            <div className="space-y-8 bg-white border border-ink-800/10 p-8">
              {visibleKeys.length === 0 && (
                <p className="text-ink-800/50 italic">No content keys match your search.</p>
              )}
              {visibleKeys.map((key) => {
                const value = draft[key] ?? "";
                const long = isLongField(key, value);
                const changed = draft[key] !== entries[key];
                return (
                  <div key={key} className={`grid md:grid-cols-12 gap-6 pb-7 border-b border-ink-800/10 last:border-0 last:pb-0`} data-testid={`admin-field-${key}`}>
                    <label className="md:col-span-3">
                      <code className="text-xs text-ink-800/80 font-mono break-all">{key}</code>
                      {changed && (
                        <span className="block mt-2 text-[10px] uppercase tracking-[0.22em] text-gold-600">Modified</span>
                      )}
                    </label>
                    <div className="md:col-span-9">
                      {long ? (
                        <textarea
                          value={value}
                          rows={Math.min(20, Math.max(6, value.split("\n").length + 1))}
                          onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
                          className="w-full border border-ink-800/15 p-4 text-sm font-sans focus:outline-none focus:border-gold-600 leading-relaxed"
                        />
                      ) : (
                        <input
                          type="text"
                          value={value}
                          onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
                          className="w-full border border-ink-800/15 px-4 py-2.5 text-sm focus:outline-none focus:border-gold-600"
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {view === "messages" && (
          <div data-testid="admin-messages-view">
            <div className="flex items-end justify-between mb-10 gap-4 flex-wrap">
              <div>
                <div className="eyebrow mb-3">Inbox</div>
                <h1 className="font-serif text-4xl">Contact Messages</h1>
                <p className="mt-2 text-ink-800/60 text-sm">
                  {messages.length} total · {unreadCount} unread
                </p>
              </div>
              <button onClick={loadMessages} className="btn-outline-ink text-xs" data-testid="admin-refresh-messages">
                <i className="fa-solid fa-arrows-rotate" /> Refresh
              </button>
            </div>

            <div className="space-y-4">
              {messages.length === 0 && (
                <div className="bg-white border border-ink-800/10 p-12 text-center text-ink-800/50">
                  <i className="fa-solid fa-inbox text-4xl mb-4 text-gold-500" />
                  <p>No messages yet.</p>
                </div>
              )}
              {messages.map((m) => (
                <article
                  key={m.id}
                  className={`bg-white border p-6 ${m.read ? "border-ink-800/10" : "border-gold-500"}`}
                  data-testid={`admin-message-${m.id}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-serif text-xl">{m.name}</h3>
                        {!m.read && (
                          <span className="text-[10px] uppercase tracking-[0.22em] text-gold-600 bg-gold-500/15 px-2 py-0.5">New</span>
                        )}
                      </div>
                      <div className="text-xs text-ink-800/60 flex flex-wrap gap-3">
                        <a href={`mailto:${m.email}`} className="hover:text-gold-600">{m.email}</a>
                        <span>·</span>
                        <span>{new Date(m.created_at).toLocaleString()}</span>
                      </div>
                      {m.subject && (
                        <div className="mt-3 text-sm uppercase tracking-[0.18em] text-ink-800/70">{m.subject}</div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {!m.read && (
                        <button onClick={() => markRead(m.id)} className="text-xs uppercase tracking-[0.2em] text-ink-800/70 hover:text-gold-600" data-testid={`admin-message-read-${m.id}`}>
                          <i className="fa-solid fa-check" /> Mark read
                        </button>
                      )}
                      <button onClick={() => deleteMessage(m.id)} className="text-xs uppercase tracking-[0.2em] text-red-700 hover:text-red-900" data-testid={`admin-message-delete-${m.id}`}>
                        <i className="fa-solid fa-trash" /> Delete
                      </button>
                    </div>
                  </div>
                  <p className="mt-5 whitespace-pre-wrap text-ink-800/85 leading-relaxed">{m.message}</p>
                </article>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
